'use client';

import Head from 'next/head';

/* eslint-disable @next/next/no-img-element */
import { Media, MediaBlock } from '@/payload-types';
import { aspectMap, generateSrcSet } from '@/utils/generateImageSrcSizes';

type Props = {
  className?: string;
  image: (number | null) | Media;
  aspectRatio:
    | NonNullable<MediaBlock['medias']>[number]['aspectRatio']
    | 'employee'
    | 'trueSquare';
  enableZoom?: boolean;
  priority?: boolean;
  responsiveWidths?: {
    mobile: number;
    desktop: number;
    largeDesktop: number;
  };
};

import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import cssBreakpoints from '@/cssBreakpoints';
import { cn } from '@/utils/cn';

import styles from './ImageMedia.module.scss';

export const ImageMedia: React.FC<Props> = (props) => {
  const [zoomed, setZoomed] = useState(false);
  const [zoomTransform, setZoomTransform] = useState<{
    x: number;
    y: number;
    scale: number;
  }>({ x: 0, y: 0, scale: 1 });
  const [clientWidth, setClientWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const {
    aspectRatio,
    className,
    enableZoom,
    image,
    priority = false,
    responsiveWidths = {
      mobile: 343,
      desktop: 1352,
      largeDesktop: 2256,
    },
  } = props;
  const { alt, height, sizes, url } = image as Media;

  const ratio = aspectRatio ? aspectRatio : 'none';
  const aspectMapped = aspectMap[ratio];
  const srcSet = sizes ? generateSrcSet(sizes, ratio) : undefined;

  const getBestSizeFor = (targetWidth: number) => {
    if (!aspectMapped || !sizes) return undefined;

    return aspectMapped
      .map((key) => sizes[key])
      .filter(
        (s): s is { width: number; height: number } => !!s?.width && !!s?.height
      )
      .sort((a, b) => a.width - b.width)
      .find((s) => s.width >= targetWidth);
  };

  const generatedSizes = `(max-width: 768px) ${responsiveWidths.mobile}px, (max-width: 1919px) ${responsiveWidths.desktop}px, ${responsiveWidths.largeDesktop}px`;

  const handleZoom = () => {
    if (enableZoom) {
      setZoomed((prev) => !prev);
    }
  };

  // 1. Get target width based on current viewport
  const layoutWidth =
    clientWidth && clientWidth <= 768
      ? responsiveWidths.mobile
      : clientWidth && clientWidth <= 1919
        ? responsiveWidths.desktop
        : responsiveWidths.largeDesktop;

  // 2. Find best image for that layout width
  const layoutBestFit = getBestSizeFor(layoutWidth);

  // 3. Fallback to original dimensions if nothing matched
  const finalWidth = layoutBestFit?.width ?? layoutWidth;
  const finalHeight =
    layoutBestFit && layoutBestFit.width && layoutBestFit.height
      ? Math.round((layoutBestFit.height / layoutBestFit.width) * finalWidth)
      : (height ?? undefined);

  // Close zoom on outside click
  useEffect(() => {
    if (!zoomed) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setZoomed(false);
      }
    };

    const handleScroll = () => setZoomed(false);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [zoomed]);

  useEffect(() => {
    const handleResize = () => {
      setClientWidth(window.innerWidth);
    };
    if (typeof window !== 'undefined') {
      setClientWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!zoomed || !containerRef.current || !clientWidth) return;

    const imageEl = containerRef.current;
    const rect = imageEl.getBoundingClientRect();

    const imageWidth = rect.width;
    const imageHeight = rect.height;

    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;

    const imageCenterX = rect.left + imageWidth / 2;
    const imageCenterY = rect.top + imageHeight / 2;

    const dx = viewportCenterX - imageCenterX;
    const dy = viewportCenterY - imageCenterY;

    const maxScale = Math.min((0.9 * clientWidth) / imageWidth, 1.7);
    const scale = maxScale > 1 ? maxScale : 1;

    setZoomTransform({ x: dx, y: dy, scale });
  }, [zoomed, clientWidth]);

  if (!url || !sizes) {
    return null;
  }

  return clientWidth &&
    clientWidth > cssBreakpoints.mobileSmall &&
    enableZoom ? (
    <>
      <Head>
        <link
          rel='preload'
          href={url}
          imageSrcSet={srcSet}
          as='image'
          sizes={generatedSizes}
        />
      </Head>
      {zoomed && (
        <div className={styles.zoomedBg} onClick={() => setZoomed(false)} />
      )}
      <motion.div
        ref={containerRef}
        role='button'
        className={cn(styles.zoom, { [styles.zoomed]: zoomed })}
        onClick={handleZoom}
        animate={{
          scale: zoomed ? zoomTransform.scale : 1,
          x: zoomed ? zoomTransform.x : 0,
          y: zoomed ? zoomTransform.y : 0,
          zIndex: zoomed ? 1010 : 1,
        }}
        initial={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{ willChange: 'transform' }}
      >
        <img
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          className={cn(styles.image, styles[ratio], className)}
          src={url}
          {...(finalWidth && { width: finalWidth })}
          {...(finalHeight && { height: finalHeight })}
          {...(srcSet && { srcSet })}
          {...(generatedSizes && { sizes: generatedSizes })}
          alt={alt ?? ''}
        />
      </motion.div>
    </>
  ) : (
    <>
      <Head>
        <link
          rel='preload'
          href={url}
          imageSrcSet={srcSet}
          as='image'
          sizes={generatedSizes}
        />
      </Head>
      <img
        className={cn(styles.image, styles[ratio], className)}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        src={url}
        {...(finalWidth && { width: finalWidth })}
        {...(finalHeight && { height: finalHeight })}
        {...(srcSet && { srcSet })}
        {...(generatedSizes && { sizes: generatedSizes })}
        alt={alt ?? ''}
      />
    </>
  );
};
